import { X } from 'lucide-react';

import { BULK_ACTIONS } from '../../features/org-orders/bulkActions/registry';
import type { BulkActionBarContext } from '../../features/org-orders/bulkActions/types';
import './OrderBulkActionsBar.scss';

interface OrderBulkActionsBarProps {
  selectedRows: any[];
  /** Selected rows that live on a page the admin isn't currently viewing. */
  selectedOffPage: number;
  onClear: () => void;
  onExport: () => void;
  isExporting: boolean;
  onApplyStatus: (status: string, rows: any[]) => Promise<void>;
  isApplyingStatus: boolean;
}

/**
 * Bulk-action toolbar for the org orders table. A thin driver: it renders the
 * selection chrome and every registered bulk action available for the selection.
 * Each action (export, note, status, …) owns its own trigger + dialog.
 */
export function OrderBulkActionsBar({
  selectedRows,
  selectedOffPage,
  onClear,
  onExport,
  isExporting,
  onApplyStatus,
  isApplyingStatus,
}: OrderBulkActionsBarProps) {
  const count = selectedRows.length;

  const ctx: BulkActionBarContext = {
    rows: selectedRows,
    count,
    selectedOffPage,
    onClearSelection: onClear,
    onExport,
    isExporting,
    onApplyStatus,
    isApplyingStatus,
  };

  const actions = BULK_ACTIONS.filter((action) => action.isAvailable(ctx));

  return (
    <div className="bulk-bar" role="region" aria-label="Bulk actions for selected loads">
      <div className="bulk-bar__count">
        <strong>
          {count} load{count === 1 ? '' : 's'} selected
        </strong>

        {selectedOffPage > 0 && (
          <span className="bulk-bar__off-page">{selectedOffPage} on other pages</span>
        )}
      </div>

      <div className="bulk-bar__actions">
        {actions.map((action) => (
          <action.Component key={action.id} ctx={ctx} />
        ))}

        <button type="button" className="btn btn--ghost btn--sm" onClick={onClear}>
          <X size={14} />
          Clear selection
        </button>
      </div>

      <p className="bulk-bar__hint">
        These actions apply to all {count} selected load
        {count === 1 ? '' : 's'}. The ⋮ menu and buttons inside a row, including Download and Send
        BOL, always act on that one load only.
      </p>
    </div>
  );
}
